import React from 'react';
import NProgress from 'nprogress';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { ZodError } from 'zod';
import { logEvent } from 'firebase/analytics';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { useUser } from '../../../hooks';
import { countries, firestore, loadAnalytics, storage } from '../../../lib';
import { Button } from '../../commons';
import { DropArea } from '../../commons/drag-and-drop';
import { Input, RadioCheckInput, Select } from '../../inputs';
import { Applicant, applicantSchema, Application } from '../../../models';

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

interface ContactFormProps {
  applicant: Applicant;
  application: Application;
  isReadOnly?: boolean;
}

export default function ContactForm({
  application,
  applicant,
  isReadOnly = false,
}: ContactFormProps) {
  const router = useRouter();
  const [user] = useUser();
  const [file, setFile] = React.useState<File | null>(null);
  const [age, setAge] = React.useState(0);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());
    let _applicant: Partial<Applicant> | null = null;
    try {
      _applicant = applicantSchema.parse(data);
    } catch (err) {
      if (err instanceof ZodError) {
        return toastError(err.issues[0].message);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E31'
      );
    }
    if (age < 18) {
      return toastError('You must be 18 or older to apply');
    }
    if (!applicant?.birthCertificatePassport && !file) {
      return toastError('You must upload your Birth certificate / Passport');
    }
    NProgress.start();
    let url = applicant?.birthCertificatePassport ?? '';
    try {
      if (file) {
        const fileName = file.name.split('.');
        const fileRef = ref(
          storage,
          `/applications/${application?.id}/birth-certificate-passport-${new Date().getTime()}.${
            fileName[fileName.length - 1]
          }`
        );
        await uploadBytes(fileRef, file);
        url = await getDownloadURL(fileRef);
      }
    } catch (err) {
      NProgress.done();
      return toastError(
        'Error uploading Birth certificate / Passport file, check your internet. CODE: E32'
      );
    }
    try {
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '3',
        applicant: {
          ..._applicant,
          birthCertificatePassport: url,
        },
      });
      logEvent(loadAnalytics(), 'step_completed', {
        step: '3',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '3',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        `/request-scholarship/4`
      );
    } catch (err) {
      NProgress.done();
      posthog?.captureException(err);
      Sentry.addBreadcrumb({ level: 'error', category: 'step-3' });
      Sentry.captureException(err);
      return toastError(
        'An error updating your application has occurred, please try again later. CODE: E33'
      );
    }
  };
  const onDrop = React.useCallback((acceptedFiles: File[]): void => {
    const [_file] = acceptedFiles;
    if (_file.size > 10000000) {
      return console.error('Mas 10MB');
    }
    setFile(_file);
  }, []);

  React.useEffect(
    function syncAge() {
      if (!applicant?.birthDate) return;
      const parts = applicant.birthDate.split('-').map((p) => parseInt(p, 10));
      const date = dayjs(new Date(parts[0], parts[1] - 1, parts[2]));
      const today = dayjs();
      setAge(today.diff(date, 'year'));
    },
    [applicant?.birthDate]
  );

  const calculateAge = (e: React.ChangeEvent<HTMLInputElement>) => {
    const str = e.currentTarget.value;
    if (!str || str.trim() === '') return;
    const parts = str.split('-').map((p) => parseInt(p, 10));
    const date = dayjs(new Date(parts[0], parts[1] - 1, parts[2]));
    const today = dayjs();
    setAge(today.diff(date, 'year'));
  };

  if (!application) return null;

  return (
    <form method="POST" onSubmit={onSubmit}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Input
          label="Name"
          type="text"
          name="firstName"
          placeholder="Name"
          required
          defaultValue={applicant?.firstName ?? user?.firstName ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
        />
        <Input
          label="Middle Name"
          type="text"
          name="middleName"
          placeholder="Middle Name"
          defaultValue={applicant?.middleName ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
        />
        <Input
          label="Surname"
          type="text"
          name="surname"
          placeholder="Surname"
          required
          defaultValue={applicant?.surname ?? user?.lastName ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
        />
      </div>
      <div className="mt-8">
        <RadioCheckInput
          type="radio"
          name="gender"
          value="male"
          label="Male"
          labelClassName="font-bold text-[20px] text-neutral-500"
          defaultChecked={applicant?.gender === 'male'}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          className="mr-4"
          required
        />
        <RadioCheckInput
          type="radio"
          name="gender"
          value="female"
          label="Female"
          labelClassName="font-bold text-[20px] text-neutral-500"
          defaultChecked={applicant?.gender === 'female'}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          className="mr-4"
          required
        />
      </div>
      <div className="mt-8">
        {isReadOnly ? null : (
          <DropArea
            label={file ? file.name : 'Birth certificate / Passport'}
            override={!!file}
            onDrop={onDrop}
            accept={{
              // 'image/*': ['.jpeg', '.jpg', '.png'],
              'application/pdf': ['.pdf'],
            }}
          />
        )}
        {applicant?.birthCertificatePassport ? (
          <a
            href={applicant.birthCertificatePassport}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] text-neutral-400 mb-8"
          >
            Preview File (Birth certificate / Passport)
          </a>
        ) : null}
      </div>
      <div className="mt-8">
        <Input
          label="Birth Date"
          type="date"
          name="birthDate"
          defaultValue={applicant?.birthDate ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          onChange={calculateAge}
          placeholder="Birth Date"
          required
        />
        <p className="-mt-3 text-[13px] text-neutral-400">
          Age will be computed automatically
        </p>
      </div>
      <div className="mt-8">
        <Input
          label="Age"
          type="text"
          name="age"
          value={age > 0 ? String(age) : ''}
          disabled
          readOnly
          placeholder="Age"
        />
        {age > 0 && age < 18 ? (
          <p className="-mt-3 text-[13px] text-red-400">
            You must be 18 years old or above to apply
          </p>
        ) : null}
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isReadOnly ? (
          <Input
            type="text"
            label="Nationality"
            defaultValue={applicant?.nationality}
            disabled
            readOnly
          />
        ) : (
          <Select
            label="Nationality"
            name="nationality"
            defaultValue={applicant?.nationality ?? 'none'}
            disabled={isReadOnly}
            required
          >
            <option value="none" disabled>
              Select a nationality
            </option>
            {countries.map((country) => (
              <option value={country.name} key={country.code}>
                {country.name}
              </option>
            ))}
          </Select>
        )}
        <Input
          label="Parish of birth"
          type="text"
          name="parish"
          defaultValue={applicant?.parish ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          placeholder="Parish of birth"
          required
        />
        <Input
          label="Address"
          type="text"
          name="address"
          defaultValue={applicant?.address ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          placeholder="Address"
          required
        />
        <Input
          label="Mailing Address (if different from address)"
          type="text"
          name="mailingAddress"
          defaultValue={applicant?.mailingAddress ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          placeholder="Mailing Address (if different from address)"
        />
        <Input
          label="Home Phone"
          type="tel"
          name="homePhone"
          defaultValue={applicant?.homePhone ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          placeholder="Home Phone"
        />
        <Input
          label="Cellphone"
          type="tel"
          name="cellphone"
          defaultValue={applicant?.cellphone ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          placeholder="Cellphone"
        />
        <Input
          label="Email"
          type="email"
          name="email"
          placeholder="Email"
          required
          defaultValue={applicant?.email ?? user?.email ?? ''}
          disabled={isReadOnly}
          readOnly={isReadOnly}
        />
      </div>
      {isReadOnly ? null : (
        <div className="mt-8 flex">
          <Button type="submit" color="blue" className="ml-auto w-[135px]">
            Continue
          </Button>
        </div>
      )}
    </form>
  );
}
